import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Star } from "lucide-react";
import { money } from "@/lib/api";

export const ListingCard = ({ item }) => {
  const navigate = useNavigate();
  const isTiered = item.billing === "tiered";
  const weekPrices = item.week_prices || [];
  const [term, setTerm] = useState(1);

  return (
  <Link
    to={`/listing/${item.id}`}
    data-testid={`listing-card-${item.id}`}
    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:border-royal/50 hover:shadow-xl hover:shadow-royal/10"
  >
    <div className="relative h-56 shrink-0 overflow-hidden">
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
      />
      <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-ink backdrop-blur">
        {item.category}
      </span>
      {item.popular && (
        <span
          data-testid={`popular-badge-${item.id}`}
          className="absolute right-4 top-4 rounded-full bg-gold px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-ink shadow-md shadow-gold/30"
        >
          Most Popular
        </span>
      )}
      {item.category !== "Tote Packages" && (
        <span
          data-testid={`price-badge-${item.id}`}
          className="absolute bottom-4 left-4 rounded-full bg-royal px-3.5 py-1.5 font-display text-sm font-extrabold text-white shadow-lg shadow-royal/40 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-105"
        >
          {item.billing === "call" ? (
            <span className="text-xs">Call for pricing</span>
          ) : (
            <>
              {money(item.billing === "once" ? item.flat_rate : item.billing === "tiered" ? (item.week_prices?.[0] || 0) : item.billing === "weekly" ? item.weekly_rate : item.daily_rate)}
              <span className="ml-1 text-[11px] font-semibold text-white/75">
                {item.billing === "once" ? "one-time" : `/ ${item.billing === "weekly" || item.billing === "tiered" ? "week" : "day"}`}
              </span>
            </>
          )}
        </span>
      )}
    </div>
    <div className="flex flex-1 flex-col p-5">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-royal">{item.brand}</p>
        <p className="flex items-center gap-1 text-xs font-semibold text-ink">
          <Star className="h-3.5 w-3.5 fill-gold text-gold" /> {item.rating}
        </p>
      </div>
      <h3 className="mt-2 font-display text-lg font-bold leading-snug text-ink">{item.title}</h3>
      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
        <MapPin className="h-3.5 w-3.5" /> {item.location}
      </p>
      <div className="mt-5 border-t border-slate-100 pt-4">
        {isTiered ? (
          <div className="flex items-center gap-2" onClick={(e) => e.preventDefault()}>
            <select
              value={term}
              onChange={(e) => setTerm(Number(e.target.value))}
              data-testid={`term-select-${item.id}`}
              className="h-10 flex-1 rounded-xl border border-slate-200 bg-paper px-3 text-xs font-semibold text-ink outline-none transition-colors duration-200 focus:border-royal"
            >
              {weekPrices.map((p, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1} week{i > 0 ? "s" : ""} — {money(p)}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid={`book-button-${item.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/listing/${item.id}?weeks=${term}`);
              }}
              className="flex h-10 items-center gap-1.5 rounded-xl bg-royal px-4 text-xs font-bold text-white transition-all duration-200 hover:bg-royal-dark hover:shadow-md hover:shadow-royal/25"
            >
              Book <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              {item.billing === "once" ? "Yours to keep" : item.billing === "weekly" ? "7 days included" : item.billing === "dual" ? `With package ${money(item.weekly_rate)}/wk` : item.billing === "call" ? "Custom quote · 75+ totes" : "Per-day billing"}
            </p>
            <span className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-ink transition-all duration-300 group-hover:border-royal group-hover:bg-royal group-hover:text-white">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </div>
        )}
      </div>
    </div>
  </Link>
  );
};
